import { CHARTBEAT_RECOMMENDATION_CONFIG } from "@config/chartBeatRecommendation";
import fetchBHApi from "@helper/fetchBHApi";
import logger from "@src/logger/Logger";
import { ProcessedArticleData } from "@transformer/useOSResponse";

type MoreArticleFeedsResponse = {
  articlePaths: string[];
  isPlaceholder: boolean;
};

type GetArticlesByIdsApiResponse = {
  data?: ProcessedArticleData[];
};

type ChartBeatDirectResponse = {
  data: Array<{ path: string }>;
};

/**
 * Fetch ChartBeat recommendations directly from front-end
 * According to ChartBeat User Segment Personalized API docs:
 * https://docs.chartbeat.com/recommendation-api/X7CUmPxqsRQEsqB0TjlE/recommendation-api/user-segment-personalized-articles
 *
 * Key requirements:
 * - These endpoints MUST be called directly from the front-end (client-side)
 * - They rely on user data (device type and geographic location) in client-side request headers
 * - Response caching should NOT be used as they are personalized per user
 * - Preserve cb_rec query parameter for click tracking
 *
 * Device-tailored endpoint: Returns trending articles for users on same device type
 * Region-tailored endpoint: Returns trending articles for users in same geographic region
 */
export async function getMoreChartBeatRecommendedArticleFeeds(): Promise<MoreArticleFeedsResponse> {
  try {
    // Build ChartBeat API URL - use device-tailored endpoint
    // This endpoint automatically infers device type from the browser making the request
    const chartbeatUrl = new URL(CHARTBEAT_RECOMMENDATION_CONFIG.endpoint);

    // Set required query parameters
    chartbeatUrl.searchParams.set("host", CHARTBEAT_RECOMMENDATION_CONFIG.host);
    chartbeatUrl.searchParams.set("path", CHARTBEAT_RECOMMENDATION_CONFIG.path);
    chartbeatUrl.searchParams.set(
      "limit",
      CHARTBEAT_RECOMMENDATION_CONFIG.limit,
    );
    chartbeatUrl.searchParams.set(
      "exclude_sections",
      CHARTBEAT_RECOMMENDATION_CONFIG.excludeSections,
    );

    // Fetch directly from ChartBeat API
    // The browser automatically includes device info in request headers
    // ChartBeat also infers geographic location from IP address
    const response = await fetch(chartbeatUrl.toString(), {
      method: "GET",
      // Don't cache - responses are personalized per user
      cache: "no-store",
    });
    if (!response.ok) {
      throw new Error(
        `HTTP error fetching ChartBeat recommendations! Status: ${response.status}`,
      );
    }

    const recommendations = (await response.json()) as ChartBeatDirectResponse;

    // Detect if response contains placeholder recommendations
    const isPlaceholder =
      response.headers.get("x-charbeat-recommendations-placeholder") ===
        "true" || response.headers.get("cache-control") === "no-store";

    if (isPlaceholder) {
      logger.debug(
        "ChartBeat returned placeholder recommendations - tailored results coming soon",
        {
          cacheControl: response.headers.get("cache-control"),
          isPlaceholder,
        },
      );
    }

    /**
     * IMPORTANT: Preserve cb_rec query parameter in article paths
     * According to ChartBeat docs: "Please ensure that these parameters are included
     * in the Chartbeat-generated recommended page URLs presented to users in your
     * completed recirculation modules, or wherever you present our recommendations to users."
     *
     * The cb_rec parameter is used for:
     * - Tracking clicks on recommended articles for reporting
     * - Identifying which customizations work best
     * - Improving recommendations over time
     */
    const articlePaths = (recommendations.data as { path: string }[]).map(
      ({ path }) => path.replace("beritaharian.sg", ""),
    );

    return { articlePaths, isPlaceholder };
  } catch (error) {
    logger.error("Failed to fetch ChartBeat recommendations from direct API", {
      error: (error as Error).message,
    });
    return {
      articlePaths: [],
      isPlaceholder: false,
    };
  }
}

/**
 * Fetch article details by their paths
 * Note: These are fetched separately from the recommendation tracking
 * The article data is resolved independently from the cb_rec tracking
 */
export async function fetchArticlesByPaths(
  articlePaths: string[],
): Promise<ProcessedArticleData[]> {
  try {
    const response = await fetchBHApi<GetArticlesByIdsApiResponse>(
      `get-articles-by-paths`,
      "POST",
      { articlePaths },
    );
    return response.data || [];
  } catch (error) {
    logger.error(
      "Failed to fetch articles by paths for chart beat recommendations",
      {
        error: (error as Error).message,
        articlePaths,
      },
    );
    return [] as ProcessedArticleData[];
  }
}
